import { useState, useEffect } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Plug,
  Server,
  Activity,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  Settings,
  ExternalLink,
  Shield,
  Zap,
  Database,
  FileText,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { IntegrationConnection } from '@/lib/authorization/types';
import { format, parseISO } from 'date-fns';

const INTEGRATION_CONFIGS = {
  epic_fhir: {
    name: 'Epic SMART-on-FHIR',
    description: 'Connect to Epic EHR for patient data and clinical workflows',
    icon: Database,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    features: [
      'Patient demographics sync',
      'Clinical notes access',
      'Order integration',
      'Real-time ADT events'
    ],
    setupSteps: [
      'Register as SMART-on-FHIR app with Epic',
      'Configure OAuth 2.0 credentials',
      'Map patient identifiers',
      'Test with sandbox environment'
    ]
  },
  epa: {
    name: 'Electronic Prior Authorization (ePA)',
    description: 'Standards-based electronic prior auth with X12 278/FHIR',
    icon: FileText,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    features: [
      'X12 278 transactions',
      'FHIR Prior Auth Guide',
      'Real-time auth status',
      'Attachment submission'
    ],
    setupSteps: [
      'Connect to clearinghouse',
      'Configure payer-specific rules',
      'Map CPT to auth requirements',
      'Enable real-time response handling'
    ]
  },
  clearinghouse: {
    name: 'Clearinghouse',
    description: 'EDI transaction processing for claims and eligibility',
    icon: Server,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    features: [
      '837/835 claims processing',
      '270/271 eligibility',
      '276/277 claim status',
      'ERA/EFT management'
    ],
    setupSteps: [
      'Establish SFTP/API connection',
      'Configure enrollment IDs',
      'Set up trading partners',
      'Test transaction flow'
    ]
  }
};

const STATUS_CONFIG = {
  connected: { icon: CheckCircle2, color: 'text-primary', label: 'Connected' },
  pending: { icon: Clock, color: 'text-amber-500', label: 'Pending Setup' },
  error: { icon: XCircle, color: 'text-red-500', label: 'Error' },
  disabled: { icon: AlertCircle, color: 'text-slate-500', label: 'Disabled' },
};

export default function Integrations() {
  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<keyof typeof INTEGRATION_CONFIGS | null>(null);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const { data, error } = await supabase
        .from('integration_connections')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setConnections((data as IntegrationConnection[]) || []);
    } catch (error) {
      console.error('Error fetching connections:', error);
      // Don't show error for empty state
    } finally {
      setLoading(false);
    }
  };

  const getConnectionByType = (type: keyof typeof INTEGRATION_CONFIGS) => {
    return connections.find(c => c.integration_type === type);
  };

  const openConfigDialog = (type: keyof typeof INTEGRATION_CONFIGS) => {
    setSelectedIntegration(type);
    setConfigDialogOpen(true);
  };

  const handleTestConnection = async (connectionId: string) => {
    toast.info('Testing connection...');
    // Simulate test
    setTimeout(() => {
      toast.success('Connection test successful');
    }, 1500);
  };

  if (loading) {
    return (
      <OpsLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse text-muted-foreground">Loading integrations...</div>
        </div>
      </OpsLayout>
    );
  }

  return (
    <OpsLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold">Integrations</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Connect to EHR systems, payer networks, and healthcare data exchanges
          </p>
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(INTEGRATION_CONFIGS).map(([key, config]) => {
            const connection = getConnectionByType(key as keyof typeof INTEGRATION_CONFIGS);
            const status = connection?.status || 'pending';
            const statusConfig = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
            const Icon = config.icon;
            const StatusIcon = statusConfig.icon;

            return (
              <Card key={key} className="relative overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn('p-2 rounded-lg', config.bgColor)}>
                        <Icon className={cn('w-5 h-5', config.color)} />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">{config.name}</h3>
                        <div className="flex items-center gap-1 mt-1">
                          <StatusIcon className={cn('w-3 h-3', statusConfig.color)} />
                          <span className={cn('text-xs', statusConfig.color)}>
                            {statusConfig.label}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => openConfigDialog(key as keyof typeof INTEGRATION_CONFIGS)}
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Separator />

        {/* Integration Details */}
        <Tabs defaultValue="epic_fhir" className="space-y-6">
          <TabsList>
            <TabsTrigger value="epic_fhir">Epic FHIR</TabsTrigger>
            <TabsTrigger value="epa">ePA</TabsTrigger>
            <TabsTrigger value="clearinghouse">Clearinghouse</TabsTrigger>
          </TabsList>

          {Object.entries(INTEGRATION_CONFIGS).map(([key, config]) => {
            const connection = getConnectionByType(key as keyof typeof INTEGRATION_CONFIGS);
            const Icon = config.icon;

            return (
              <TabsContent key={key} value={key} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Info Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className={cn('p-3 rounded-lg', config.bgColor)}>
                          <Icon className={cn('w-6 h-6', config.color)} />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{config.name}</CardTitle>
                          <CardDescription>{config.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-medium text-sm mb-2">Features</h4>
                        <ul className="space-y-2">
                          {config.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <CheckCircle2 className="w-4 h-4 text-primary" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Status Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Connection Status</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {connection ? (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Status</span>
                            <Badge className={cn(
                              connection.status === 'connected' && 'bg-primary/10 text-primary',
                              connection.status === 'pending' && 'bg-amber-500/10 text-amber-500',
                              connection.status === 'error' && 'bg-red-500/10 text-red-500',
                              connection.status === 'disabled' && 'bg-slate-500/10 text-slate-500',
                            )}>
                              {STATUS_CONFIG[connection.status as keyof typeof STATUS_CONFIG]?.label}
                            </Badge>
                          </div>
                          {connection.last_sync_at && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Last Sync</span>
                              <span className="text-sm">
                                {format(parseISO(connection.last_sync_at), 'PPp')}
                              </span>
                            </div>
                          )}
                          {connection.error_message && (
                            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                              <p className="text-sm text-red-500">{connection.error_message}</p>
                            </div>
                          )}
                          <div className="flex gap-2 pt-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleTestConnection(connection.id)}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Test Connection
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openConfigDialog(key as keyof typeof INTEGRATION_CONFIGS)}
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Configure
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-6">
                          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                          <h4 className="font-medium">Not Configured</h4>
                          <p className="text-sm text-muted-foreground mt-1 mb-4">
                            This integration has not been set up yet
                          </p>
                          <Button onClick={() => openConfigDialog(key as keyof typeof INTEGRATION_CONFIGS)}>
                            <Plug className="w-4 h-4 mr-2" />
                            Set Up Integration
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Setup Steps */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Setup Guide</CardTitle>
                    <CardDescription>
                      Follow these steps to complete the integration
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {config.setupSteps.map((step, i) => (
                        <div key={i} className="flex items-start gap-4">
                          <div className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0',
                            connection?.status === 'connected' 
                              ? 'bg-primary/10 text-primary' 
                              : 'bg-muted text-muted-foreground'
                          )}>
                            {connection?.status === 'connected' ? (
                              <CheckCircle2 className="w-4 h-4" />
                            ) : (
                              i + 1
                            )}
                          </div>
                          <div className="flex-1 pt-1">
                            <p className="text-sm">{step}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>

        {/* ePA Readiness Section */}
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-blue-500" />
              <div>
                <CardTitle>ePA Readiness Checklist</CardTitle>
                <CardDescription>
                  Preparation for CMS Electronic Prior Authorization Rule compliance
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Technical Requirements</h4>
                <div className="space-y-2">
                  {[
                    'X12 278 transaction support',
                    'FHIR R4 capability',
                    'Da Vinci Prior Auth IG compliance',
                    'Real-time response processing'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded border border-muted-foreground/30" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Payer Readiness</h4>
                <div className="space-y-2">
                  {[
                    'Medicare Advantage ePA support',
                    'Medicaid managed care enrollment',
                    'Commercial payer connections',
                    'Attachment submission workflow'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded border border-muted-foreground/30" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm font-medium">CMS Deadline: January 1, 2027</p>
                  <p className="text-xs text-muted-foreground">
                    Impacted payers must support electronic prior authorization for specific services
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuration Dialog */}
        <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Configure {selectedIntegration && INTEGRATION_CONFIGS[selectedIntegration]?.name}
              </DialogTitle>
              <DialogDescription>
                Enter your connection credentials and settings
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {selectedIntegration === 'epic_fhir' && (
                <>
                  <div className="space-y-2">
                    <Label>Client ID</Label>
                    <Input placeholder="Enter Epic client ID" />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret</Label>
                    <Input type="password" placeholder="Enter client secret" />
                  </div>
                  <div className="space-y-2">
                    <Label>FHIR Base URL</Label>
                    <Input placeholder="https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4" />
                  </div>
                </>
              )}
              {selectedIntegration === 'epa' && (
                <>
                  <div className="space-y-2">
                    <Label>Clearinghouse Provider</Label>
                    <Input placeholder="e.g., Change Healthcare, Availity" />
                  </div>
                  <div className="space-y-2">
                    <Label>Submitter ID</Label>
                    <Input placeholder="Enter submitter ID" />
                  </div>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input type="password" placeholder="Enter API key" />
                  </div>
                </>
              )}
              {selectedIntegration === 'clearinghouse' && (
                <>
                  <div className="space-y-2">
                    <Label>SFTP Host</Label>
                    <Input placeholder="sftp.clearinghouse.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input placeholder="Enter username" />
                  </div>
                  <div className="space-y-2">
                    <Label>SSH Key / Password</Label>
                    <Input type="password" placeholder="Enter credentials" />
                  </div>
                </>
              )}
              <div className="flex items-center justify-between pt-2">
                <Label>Enable Integration</Label>
                <Switch defaultChecked />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => {
                toast.success('Configuration saved');
                setConfigDialogOpen(false);
              }}>
                Save Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </OpsLayout>
  );
}
